// Doc-Ref: docs/features/collaborations-current.md
import { useState, useEffect } from "react";
import { Filter, RotateCcw } from "lucide-react";

import { Button } from "components/ui/button";
import { Slider } from "components/ui/slider";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import SubjectAreaFilter from "./SubjectAreaFilter";

const FiltersRow = ({ filters = {}, onFilterChange = () => { }, onReset, entityType = "institution" }) => {
  const currentYear = new Date().getFullYear();

  // Use local state to manage filters before applying
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync localFilters if the parent's filters prop changes completely (e.g. initial load or external reset)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const getCollaboratorTypeOptions = () => {
    if (entityType === "researcher") {
      return [
        { label: "Researcher", value: "researcher" },
        { label: "Institution", value: "institution" },
      ];
    } else if (entityType === "unit") {
      return [{ label: "Institution", value: "institution" }];
    } else if (entityType === "institution") {
      return [{ label: "Institution", value: "institution" }];
    }
    return [{ label: "Institution", value: "institution" }];
  };

  const handleApply = () => {
    onFilterChange(localFilters);
  };

  const handleReset = () => {
    const defaultFilters = {
      yearRange: [2014, currentYear],
      subjectArea: [],
      geography: "us",
      metric: "publications",
      collaboratorType: entityType === "researcher" ? "researcher" : "institution",
      keywordsOperator: "or",
    };
    setLocalFilters(defaultFilters);
    onFilterChange(defaultFilters);
    if (onReset) onReset();
  };

  return (
    <div className="flex flex-col gap-8 p-6">

      {/* Year Range */}
      <div className="flex flex-col gap-4">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Publication year range
        </label>
        <Slider
          min={1990}
          max={currentYear}
          value={localFilters.yearRange || [2014, currentYear]}
          onValueChange={(val) => setLocalFilters({ ...localFilters, yearRange: val })}
          className="my-1"
        />
        <div className="flex items-center justify-between bg-gray-50 rounded-md p-1 border border-gray-100 mt-2">
          <Input
            type="number"
            min={1990}
            max={currentYear}
            className="h-7 w-20 text-xs text-center bg-transparent border-none shadow-none focus-visible:ring-0 px-1"
            value={localFilters.yearRange?.[0] || 2014}
            onChange={(e) => setLocalFilters({ ...localFilters, yearRange: [Number(e.target.value), localFilters.yearRange?.[1] || currentYear] })}
          />
          <span className="text-gray-300 text-xs font-medium">-</span>
          <Input
            type="number"
            min={1990}
            max={currentYear}
            className="h-7 w-20 text-xs text-center bg-transparent border-none shadow-none focus-visible:ring-0 px-1"
            value={localFilters.yearRange?.[1] || currentYear}
            onChange={(e) => setLocalFilters({ ...localFilters, yearRange: [localFilters.yearRange?.[0] || 2014, Number(e.target.value)] })}
          />
        </div>
      </div>

      {/* Subject Area */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Subject area
        </label>
        <SubjectAreaFilter
          filters={localFilters}
          setFilters={(newVals) => setLocalFilters((prev) => ({ ...prev, ...newVals }))}
          entity="subjectArea"
        />
        {localFilters.subjectArea && localFilters.subjectArea.length >= 2 && (
          <ToggleGroup
            type="single"
            value={localFilters.keywordsOperator || 'or'}
            onValueChange={(val) => {
              if (val) setLocalFilters({ ...localFilters, keywordsOperator: val });
            }}
            className="mt-1 w-full justify-start border border-gray-100 rounded-md p-1 bg-gray-50"
          >
            <ToggleGroupItem value="or" className="flex-1 h-7 text-[11px] font-medium data-[state=on]:bg-white data-[state=on]:shadow-sm data-[state=on]:text-explore text-gray-500">
              Match Any
            </ToggleGroupItem>
            <ToggleGroupItem value="and" className="flex-1 h-7 text-[11px] font-medium data-[state=on]:bg-white data-[state=on]:shadow-sm data-[state=on]:text-explore text-gray-500">
              Match All
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>

      {/* Geography */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Geography
        </label>
        <Select
          value={localFilters.geography || "us"}
          onValueChange={(val) => setLocalFilters({ ...localFilters, geography: val })}
        >
          <SelectTrigger className="h-9 w-full bg-white border-gray-200 text-sm hover:bg-gray-50/50 transition-colors">
            <SelectValue placeholder="Select geography" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="international">International</SelectItem>
            <SelectItem value="us">United States</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Collaborator Type */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Collaborator type
        </label>
        <Select
          value={localFilters.collaboratorType || "institution"}
          onValueChange={(val) => setLocalFilters({ ...localFilters, collaboratorType: val })}
        >
          <SelectTrigger className="h-9 w-full bg-white border-gray-200 text-sm hover:bg-gray-50/50 transition-colors">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {getCollaboratorTypeOptions().map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Metric */}
      <div className="flex flex-col gap-3">
        <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
          Metric
        </label>
        <Select
          value={localFilters.metric || "publications"}
          onValueChange={(val) => setLocalFilters({ ...localFilters, metric: val })}
        >
          <SelectTrigger className="h-9 w-full bg-white border-gray-200 text-sm hover:bg-gray-50/50 transition-colors">
            <SelectValue placeholder="Select metric" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="publications">Publications</SelectItem>
            <SelectItem value="citations">Citations</SelectItem>
            <SelectItem value="fwci">FWCI</SelectItem>
            <SelectItem value="collaborators">Collaborators</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 mt-6">
        <Button onClick={handleApply} className="w-full h-10 bg-explore hover:bg-explore/90 text-white font-medium flex items-center justify-center gap-2 shadow-sm rounded-md transition-all">
          <Filter size={16} className="text-white/70" />
          Apply Filters
        </Button>
        <button
          onClick={handleReset}
          className="w-full text-xs text-gray-400 hover:text-gray-700 flex items-center justify-center gap-1.5 transition-colors mt-1"
        >
          <RotateCcw size={12} />
          Reset to default
        </button>
      </div>

    </div>
  );
};

export default FiltersRow;
